import { v4 as uuidv4 } from 'uuid';
import {
  AnalysisMessage,
  ProgressMessage,
  CompletionMessage,
  ErrorMessage,
  StageCompletionMessage,
} from '../../api/websocket/types';
import {
  ProcessingAction,
  StageMessage,
  StageStatus,
  ProcessingType,
  AnalysisResult,
  RewriteResult,
  getStageInfo,
  ProcessingState,
} from './types';
import { TemplateVariation } from '../../features/template/types';

/**
 * Creates a stage message from message content
 */
export function createStageMessage(
  content: string,
  type: StageMessage['type'] = 'info',
  details?: Record<string, unknown>
): StageMessage {
  return {
    id: uuidv4(),
    timestamp: Date.now(),
    type,
    content,
    details,
  };
}

/**
 * Determines stage status based on message type and current status
 */
export function determineStageStatus(
  messageType: AnalysisMessage['type'],
  currentStatus?: StageStatus,
  error?: { severity: 'RECOVERABLE' | 'FATAL' }
): StageStatus {
  if (messageType === 'ERROR') {
    return error?.severity === 'RECOVERABLE' ? 'error_continuing' : 'error';
  }

  switch (messageType) {
    case 'START':
      return 'in_progress';
    case 'PROGRESS':
      return 'in_progress';
    case 'COMPLETION':
      return 'completed';
    default:
      return currentStatus || 'pending';
  }
}

/**
 * Creates actions from a WebSocket message
 */
export function createActionsFromMessage(
  message: AnalysisMessage,
  processingType: ProcessingType,
  currentState: ProcessingState
): ProcessingAction[] {
  const actions: ProcessingAction[] = [];

  switch (message.type) {
    case 'START': {
      actions.push({
        type: 'START_PROCESSING',
        payload: { processingType },
      });
      break;
    }

    case 'PROGRESS': {
      const progressMessage = message as ProgressMessage;
      const { stage_name, progress, details } = progressMessage.data;
      const stageInfo = getStageInfo(stage_name);

      // Update the stage status and progress
      actions.push({
        type: 'UPDATE_STAGE',
        payload: {
          stageId: stage_name,
          updates: {
            id: stage_name,
            name: details?.message ? `${stageInfo.name}: ${details.message}` : stageInfo.name,
            status: 'in_progress',
            progress,
            details,
            isExpanded: true,
          },
        },
      });

      // Add progress message as a child item
      if (details?.message) {
        actions.push({
          type: 'ADD_MESSAGE',
          payload: {
            stageId: stage_name,
            message: createStageMessage(
              details.message as string,
              'info',
              details
            ),
          },
        });
      }
      break;
    }

    case 'STAGE_COMPLETION': {
      const completionMessage = message as StageCompletionMessage;
      const { stage_name, result } = completionMessage.data;
      const stageInfo = getStageInfo(stage_name);

      // Update stage status
      actions.push({
        type: 'UPDATE_STAGE',
        payload: {
          stageId: stage_name,
          updates: {
            name: stageInfo.name,
            status: 'completed',
            progress: 0,
          },
        },
      });

      // Add completion message as a child
      actions.push({
        type: 'ADD_MESSAGE',
        payload: {
          stageId: stage_name,
          message: createStageMessage(
            `${stageInfo.name} completed`,
            'info',
            result
          ),
        },
      });
      break;
    }

    case 'COMPLETION': {
      const completionMessage = message as CompletionMessage;
      const finalStageStatus = completionMessage.status === 'SUCCESSFUL' ? 'completed' : 'error';
      const finalStageName = completionMessage.status === 'SUCCESSFUL' ? 'Completed' : 'Completed with error';
      const finalMessageContent =
        completionMessage.status === 'SUCCESSFUL'
          ? 'Task completed successfully'
          : `Task completed with error: ${completionMessage.error?.message || 'Unknown error'}`;
      const messageType = completionMessage.status === 'SUCCESSFUL' ? 'info' : 'error';
      const shouldExpand = completionMessage.status !== 'SUCCESSFUL';
      
      // First create/update the stage
      actions.push({
        type: 'UPDATE_STAGE',
        payload: {
          stageId: 'completion',
          updates: {
            id: 'completion',
            name: finalStageName,
            status: finalStageStatus,
            progress: 0,
            isExpanded: shouldExpand,
          },
        },
      });

      // Add the message
      actions.push({
        type: 'ADD_MESSAGE',
        payload: {
          stageId: 'completion',
          message: createStageMessage(finalMessageContent, messageType),
        },
      });

      // Set expansion state after adding message
      actions.push({
        type: 'UPDATE_STAGE',
        payload: {
          stageId: 'completion',
          updates: {
            isExpanded: shouldExpand,
          },
        },
      });

      // Always dispatch SET_RESULT to ensure isProcessing is set to false
      const rawData = completionMessage.data?.result as Record<string, unknown>;
      let extractedResult: AnalysisResult | RewriteResult;
      
      if (completionMessage.status === 'SUCCESSFUL' && rawData) {
        // For successful completion, check and cast the data based on its structure
        if (processingType === 'analysis' && 
            typeof rawData.sender_bias === 'string' && 
            Array.isArray(rawData.content_biases)) {
          extractedResult = {
            sender_bias: rawData.sender_bias,
            content_biases: rawData.content_biases as string[]
          };
        } else if (processingType === 'rewrite' && 
                   Array.isArray(rawData.variations)) {
          extractedResult = {
            variations: rawData.variations as TemplateVariation[]
          };
        } else {
          // If data doesn't match expected structure, use empty result
          extractedResult = processingType === 'analysis'
            ? { sender_bias: '', content_biases: [] }
            : { variations: [] };
        }
      } else {
        // For error completion or no data, create an empty result
        extractedResult = processingType === 'analysis'
          ? { sender_bias: '', content_biases: [] }
          : { variations: [] };
      }
      
      actions.push({
        type: 'SET_RESULT',
        payload: { result: extractedResult },
      });

      break;
    }

    case 'ERROR': {
      const errorMessage = message as ErrorMessage;
      const { code, message: errorText, severity } = errorMessage.error;
      
      // Update the current stage status
      if (currentState.currentStage) {
        actions.push({
          type: 'UPDATE_STAGE',
          payload: {
            stageId: currentState.currentStage,
            updates: {
              status: severity === 'RECOVERABLE' ? 'error_continuing' : 'error',
              // Don't reset progress on recoverable errors
              progress: severity === 'RECOVERABLE' ? undefined : 0,
            },
          },
        });

        // Add error message as a child
        const formattedMessage = code === 'VALIDATION_ERROR' 
          ? `Malformed model response: ${errorText}`
          : `${errorText} (${code})`;

        actions.push({
          type: 'ADD_MESSAGE',
          payload: {
            stageId: currentState.currentStage,
            message: createStageMessage(
              formattedMessage,
              'error',
              errorMessage.error
            ),
          },
        });
      }
      break;
    }
  }

  return actions;
}

/**
 * Creates a connection status action
 */
export function createConnectionStatusAction(
  status: 'connected' | 'disconnected' | 'reconnecting'
): ProcessingAction {
  return {
    type: 'SET_CONNECTION_STATUS',
    payload: { status },
  };
} 